<template>
  <div class="multi-select">
    <ProvideStateTriggeredBySlotProps v-slot="{ turnOff, toggle, isOn }">
      <BasePopper v-click-outside="turnOff" :display="isOn">
        <a
          :class="{ 'locked-width': lockedWidth, disabled, 'btn-sm': small }"
          class="btn btn-default"
          v-on="{ click: !disabled ? toggle : () => {} }"
        >
          <div class="pull-left" data-test="selection">
            <slot>
              <slot v-if="allSelectedDisplay" name="all-selected">
                {{ $t('All Selected') }}
              </slot>
              <slot v-else-if="noSelectionDisplay" name="no-selection">
                {{ $t('No Selection') }}
              </slot>
              <slot v-else :selected="selected" name="selected-text">
                {{ selected[0].name }}
              </slot>
            </slot>
          </div>
          <div class="pull-right" data-test="dropdown">
            <span v-if="selected.length > 1 && !allSelectedDisplay" class="badge c-white tw-ml-1">
              +{{ selected.length - 1 }}
            </span>
            <span class="caret m-l-5" />
          </div>
        </a>
        <template #popper>
          <div ref="dropdownWrapper" class="base-dropdown-menu-wrapper">
            <ul class="base-dropdown-menu">
              <slot :popper-flipper="isOn" name="custom-options" />
              <slot name="select-all">
                <li class="checkbox">
                  <label :class="{ hide: !isOn }">
                    <input v-model="allSelected" name="all selected" type="checkbox" />
                    <i class="input-helper" />
                    <span>{{ $t('Select all') }}</span>
                  </label>
                </li>
              </slot>
              <li role="separator" class="divider" />
              <li v-for="option in options" :key="hash(option.name)" data-test="select-option" class="checkbox">
                <slot :option="option" :is-on="isOn" name="options">
                  <label :class="{ hide: !isOn }" :data-test="`select-option-${option.name}`">
                    <input v-model="selected" :value="option" :name="option.name" type="checkbox" />
                    <i class="input-helper" />
                    <span>{{ option.name }}</span>
                  </label>
                </slot>
              </li>
            </ul>
          </div>
        </template>
      </BasePopper>
    </ProvideStateTriggeredBySlotProps>
  </div>
</template>

<script>
import { useInfiniteScroll } from '@vueuse/core'
import BasePopper from '@/components/BasePopper.vue'
import HashMixin from '@/mixins/MethodHash'
import ProvideStateTriggeredBySlotProps from '@/components/ProvideStateTriggeredBySlotProps.vue'
// To use this component just give it in both options and value, an array of objects that contain a name property
// Ie [{name: foo}, {name: bar}], it will then on v-model have the same objects added,
// Ie [{name: foo}] if that is selected

export default {
  name: 'BaseMultiSelect',
  components: { BasePopper, ProvideStateTriggeredBySlotProps },
  mixins: [HashMixin],
  props: {
    options: {
      type: Array,
      required: true,
    },
    modelValue: {
      type: Array,
      required: true,
    },
    lockedWidth: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
    paginates: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'loadMore'],

  computed: {
    selected: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    allSelected: {
      get() {
        return this.selected.length === this.options.length
      },
      set() {
        if (this.allSelected) {
          this.selected = [] // We empty the selected list
        } else {
          this.selected = this.options
        }
      },
    },
    allSelectedDisplay() {
      return this.allSelected && this.options.length > 1
    },
    noSelectionDisplay() {
      return this.selected.length === 0
    },
  },

  mounted() {
    if (this.paginates) {
      this.bootPaginateScrollObserver()
    }
  },

  methods: {
    bootPaginateScrollObserver() {
      const loadMore = this.$refs.dropdownWrapper
      useInfiniteScroll(
        loadMore,
        () => {
          this.$emit('loadMore')
        },
        { distance: 10 },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.multi-select {
  padding: 5px 5px 5px 0;
}
.checkbox label:before,
.checkbox-inline:before,
.input-helper:after,
.input-helper:before {
  -webkit-transition: all;
  -o-transition: all;
  transition: all;
  -webkit-transition-duration: 0ms;
  transition-duration: 0ms;
}

.base-dropdown-menu-wrapper {
  overflow-y: auto;
  max-height: 448.8px;
}
</style>
